import { useToastStore, type ToastKind } from '@/stores/toastStore'
import { Close } from '@/components/ui/icons'

/**
 * Renders the toast queue once at app root. Subscribes to `toastStore` and
 * stacks dismissible banners bottom-right. Styling reuses the existing
 * `--color-status-*` tokens the rest of the app already uses for status chips,
 * so toasts match the inline error banners visually.
 *
 * Mounted once in `App` (outside the route tree) so a toast survives route
 * changes and never depends on a particular page being mounted.
 */

const kindClasses: Record<ToastKind, string> = {
  error: 'bg-status-error-bg text-status-error-text',
  success: 'bg-status-success-bg text-status-success-text',
  info: 'bg-status-info-bg text-status-info-text',
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts)
  const dismiss = useToastStore((s) => s.dismiss)

  if (toasts.length === 0) return null

  return (
    <div
      // aria-live polite so screen readers announce new toasts without stealing
      // focus; the region itself is decorative until a toast appears.
      aria-live="polite"
      className="pointer-events-none fixed right-4 bottom-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          className={`pointer-events-auto flex items-start gap-2 rounded-lg px-3 py-2 text-sm shadow-md ${kindClasses[toast.kind]}`}
        >
          <span className="flex-1">{toast.message}</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => dismiss(toast.id)}
            className="-mr-1 shrink-0 cursor-pointer rounded p-0.5 opacity-70 hover:opacity-100"
          >
            <Close size={16} />
          </button>
        </div>
      ))}
    </div>
  )
}
